/**
 * Hands raised — the Spider-Sense Factory's "yes" (MVP notes §17), and the Compact-mode candidate
 * for a jump substitute later (game design spec §15; Slice F's business, not this one's).
 *
 * Held, like the crouch: one event up, one event down, nothing while they stay up. A wrist counts
 * as raised when it is clear of the shoulder line by a calibrated margin, so a child with their
 * arms folded or scratching their head is not answering.
 *
 * How many wrists it takes is a calibrated number rather than a constant here. It defaults to one,
 * because a five-year-old answers with whichever hand is free and a detector that demanded both
 * would tell a child who answered that they had not.
 */

import type { DetectorOptions, DetectorOutcome, PoseDetector } from '../detector-types';
import { DEFAULT_POSE_CALIBRATION } from '../pose-calibration';
import { shoulderMidpoint } from '../pose-geometry';
import { POSE_LANDMARK, type PoseObservation } from '../pose-types';
import { createTrackingGate } from './tracking-gate';

const HANDS_LANDMARKS = [
  POSE_LANDMARK.leftShoulder,
  POSE_LANDMARK.rightShoulder,
  POSE_LANDMARK.leftWrist,
  POSE_LANDMARK.rightWrist,
];

const WRISTS = [POSE_LANDMARK.leftWrist, POSE_LANDMARK.rightWrist];

export function createHandsDetector(options: DetectorOptions = {}): PoseDetector {
  const calibration = options.calibration ?? DEFAULT_POSE_CALIBRATION;
  const gate = createTrackingGate({ landmarks: HANDS_LANDMARKS, tunables: options.tunables });

  const raiseMargin = calibration.bodyScale * calibration.handsRaiseScale;
  const lowerMargin = raiseMargin * calibration.hysteresisFraction;

  let raised = false;
  /**
   * Set by {@link PoseDetector.reset}: the next readable frame is a *baseline*, not an answer.
   *
   * Neutral re-acquisition (D-068(2)). A reset happens when the round changes what the body means,
   * when a grown-up re-measures the child, and when tracking comes back — never because the child
   * moved. Without this the very next frame of arms that were already up is a rising edge, and a
   * five-year-old who answered "yes" and left their arms up has the next yes/no round answered for
   * them. The keyboard cannot do that: a held key produces no second `keydown`.
   */
  let awaitingBaseline = false;

  /** How many wrists clear the shoulder line by `margin`. */
  const wristsAbove = (pose: PoseObservation, shoulderY: number, margin: number): number => {
    let count = 0;
    for (const index of WRISTS) {
      const wrist = pose.landmarks[index];
      // Frame space grows downward: a raised wrist is *above* the shoulders, so smaller y.
      if (wrist !== undefined && shoulderY - wrist.y >= margin) count += 1;
    }
    return count;
  };

  return {
    capability: 'hands',

    observe(frame): DetectorOutcome {
      const reading = gate.read(frame);
      if (reading.kind === 'blocked') {
        // A posture nobody was watching is re-acquired from neutral, exactly as after a reset: the
        // arms coming back into view is not the child raising them (recon §B.4, D-068(2)).
        if (reading.lost) {
          raised = false;
          awaitingBaseline = true;
        }
        return reading.outcome;
      }

      const shoulders = shoulderMidpoint(reading.pose);
      if (shoulders === null) return { kind: 'none' };

      const required = Math.max(1, Math.round(calibration.handsRaisedWristCount));

      if (awaitingBaseline) {
        awaitingBaseline = false;
        // Whatever the arms are already doing becomes the state we start from, silently.
        raised = wristsAbove(reading.pose, shoulders.y, raiseMargin) >= required;
        return { kind: 'none' };
      }

      if (!raised) {
        if (wristsAbove(reading.pose, shoulders.y, raiseMargin) < required) return { kind: 'none' };
        raised = true;
        return {
          kind: 'event',
          event: { type: 'PLAYER_RAISED_HANDS' },
          confidence: reading.confidence,
        };
      }

      // Coming down needs to clear the hysteresis margin, or a tiring arm chatters.
      if (wristsAbove(reading.pose, shoulders.y, lowerMargin) >= required) return { kind: 'none' };

      raised = false;
      return {
        kind: 'event',
        event: { type: 'PLAYER_LOWERED_HANDS' },
        confidence: reading.confidence,
      };
    },

    /**
     * A hole in the observation: whatever the arms did inside it was not watched (S5-1/S5-3/S5-5).
     *
     * Exactly {@link reset}'s semantic half, and deliberately **not** its gate half — how long it
     * has been since this body could be seen is not refreshed by an interval in which nothing was
     * looked at.
     */
    reacquireSilently(): void {
      raised = false;
      awaitingBaseline = true;
    },

    reset(): void {
      gate.reset();
      raised = false;
      awaitingBaseline = true;
    },
  };
}
